import { z } from 'zod';
import { ConfidenceBand, ConfidenceReason } from './requirement.js';

/**
 * The extraction output contract.
 *
 * EVIDENCE COMES FIRST in the field order, deliberately. A model asked for a
 * conclusion and then its evidence will confabulate support for whatever it
 * already said; asked to quote first, the conclusion is constrained by the text
 * it just produced. The order is part of the contract, not a formatting
 * preference.
 */

/** No floats anywhere. A band with a closed reason is actionable — "NEEDS REVIEW
 *  — interior/exterior unclear" tells a producer what to look at — where 0.63
 *  tells them nothing and invites false precision. */
export const SceneLocationSignal = z.object({
  /** The verbatim screenplay text supporting this signal. Re-located against the
   *  canonical text before any highlight renders; the model's own offsets are
   *  never trusted. */
  evidenceQuote: z.string().min(1).max(2000),

  /** Constrained to the vocabulary sent with the request. This prevents
   *  out-of-vocabulary codes; it CANNOT prevent a semantically wrong choice,
   *  which is what the human review layer exists for. */
  typeCode: z.string().min(1).max(64),
  subtypeSlugs: z.array(z.string().max(64)).max(8).default([]),
  vibeCodes: z.array(z.string().max(64)).max(8).default([]),

  intExt: z.enum(['INT', 'EXT', 'INT_EXT', 'UNKNOWN']).default('UNKNOWN'),
  timeOfDay: z.string().max(32).nullable().default(null),

  /** Free text the producer reads, never machine-evaluated. */
  creativeDescription: z.string().max(2000).nullable().default(null),

  confidenceBand: ConfidenceBand,
  confidenceReason: ConfidenceReason.nullable().default(null),
});
export type SceneLocationSignal = z.infer<typeof SceneLocationSignal>;

export const SceneExtractionOutput = z.object({
  sceneId: z.string().min(1),
  signals: z.array(SceneLocationSignal).max(10),
});
export type SceneExtractionOutput = z.infer<typeof SceneExtractionOutput>;

/**
 * One LOCATION, with every scene that needs it.
 *
 * WHY THE WHOLE SCREENPLAY GOES IN ONE REQUEST (measured 2026-09-18).
 *
 * Extraction used to ask the model about one scene at a time, which cannot see
 * that scene 1 and scene 74 are the same apartment — so grouping fell to a
 * regex over sluglines, which only ever catches literal repeats. Sending the
 * whole screenplay instead, on a real 120-scene feature:
 *
 *     one request · 9.4s · 38,481 tokens in · 3,537 out (5% of the cap)
 *     53 distinct locations, 13 of them spanning several scenes
 *     "David's New York City Apartment" -> scenes 1, 14, 19, 23, 50, 74, 76, 101, 102
 *
 * against 120 requests and no grouping at all. The output is SMALLER, not
 * larger, because grouping compresses it — 53 locations rather than 120 scene
 * entries. The estimate that said otherwise was wrong by an order of magnitude,
 * which is why the numbers above are recorded rather than reasoned about.
 *
 * `sceneRefs` are the ordinals of OUR OWN scene markers, injected into the text
 * we send. The screenplay's own numbering is unreliable — a shooting draft
 * renumbers, a first draft has none — so the reference is to a position we
 * control and can map back exactly.
 */
export const ScreenplayLocation = z.object({
  /** Evidence first: see the note at the top of this file. */
  evidenceQuote: z.string().min(1).max(2000),

  /** The scene markers this location appears in, as sent. */
  sceneRefs: z.array(z.number().int().positive()).min(1).max(400),

  name: z.string().min(1).max(255),
  typeCode: z.string().min(1).max(64),
  subtypeSlugs: z.array(z.string().max(64)).max(8).default([]),
  vibeCodes: z.array(z.string().max(64)).max(8).default([]),
  intExt: z.enum(['INT', 'EXT', 'INT_EXT', 'UNKNOWN']).default('UNKNOWN'),
  creativeDescription: z.string().max(2000).nullable().default(null),

  confidenceBand: ConfidenceBand,
  confidenceReason: ConfidenceReason.nullable().default(null),
});
export type ScreenplayLocation = z.infer<typeof ScreenplayLocation>;

export const ScreenplayExtractionOutput = z.object({
  /** Capped well above any real feature: 53 came back for a 120-scene script. */
  locations: z.array(ScreenplayLocation).max(300),
});
export type ScreenplayExtractionOutput = z.infer<typeof ScreenplayExtractionOutput>;

/**
 * Bumped whenever this shape changes. It is a component of the extraction cache
 * key, so an old cached response can never be replayed against a new schema and
 * silently validated into the wrong shape.
 */
export const OUTPUT_SCHEMA_VERSION = '2';

/**
 * The numerical acceptance gate.
 *
 * "High precision" is not a gate — it is an adjective. These are the numbers a
 * change must beat before bulk approval may ship, and they live in the contract
 * so a threshold cannot be relaxed quietly in a service file.
 */
export const EXTRACTION_QUALITY_GATE = Object.freeze({
  /** Of the requirements proposed at HIGH confidence, the share that are correct.
   *  Highest bar because HIGH is what bulk-approve acts on without reading. */
  highRequirementPrecision: 0.95,
  /** Of the location requirements actually in the screenplay, the share found. */
  locationRequirementRecall: 0.9,
  /** Quotes that do not appear in the source text. Near-zero: a fabricated quote
   *  destroys trust in every quote. */
  unsupportedEvidenceQuoteRate: 0.01,
  /** Output that fails the schema or uses an out-of-vocabulary code. Must be
   *  exactly zero — the vocabulary is sent with the request. */
  invalidSchemaOrTaxonomyRate: 0,
  /** Responses needing the single structured-output repair attempt. */
  schemaRepairRate: 0.03,
});

export interface ExtractionEvalResult {
  readonly highRequirementPrecision: number;
  readonly locationRequirementRecall: number;
  readonly unsupportedEvidenceQuoteRate: number;
  readonly invalidSchemaOrTaxonomyRate: number;
  readonly schemaRepairRate: number;
}

export interface GateFailure {
  readonly metric: string;
  readonly observed: number;
  readonly required: number;
  readonly direction: 'at_least' | 'at_most';
}

/** Returns every failure rather than the first, so one eval run tells you
 *  everything that regressed instead of one thing at a time. */
export function evaluateQualityGate(result: ExtractionEvalResult): GateFailure[] {
  const failures: GateFailure[] = [];
  const gate = EXTRACTION_QUALITY_GATE;

  if (result.highRequirementPrecision < gate.highRequirementPrecision) {
    failures.push({
      metric: 'highRequirementPrecision',
      observed: result.highRequirementPrecision,
      required: gate.highRequirementPrecision,
      direction: 'at_least',
    });
  }
  if (result.locationRequirementRecall < gate.locationRequirementRecall) {
    failures.push({
      metric: 'locationRequirementRecall',
      observed: result.locationRequirementRecall,
      required: gate.locationRequirementRecall,
      direction: 'at_least',
    });
  }
  if (result.unsupportedEvidenceQuoteRate > gate.unsupportedEvidenceQuoteRate) {
    failures.push({
      metric: 'unsupportedEvidenceQuoteRate',
      observed: result.unsupportedEvidenceQuoteRate,
      required: gate.unsupportedEvidenceQuoteRate,
      direction: 'at_most',
    });
  }
  if (result.invalidSchemaOrTaxonomyRate > gate.invalidSchemaOrTaxonomyRate) {
    failures.push({
      metric: 'invalidSchemaOrTaxonomyRate',
      observed: result.invalidSchemaOrTaxonomyRate,
      required: gate.invalidSchemaOrTaxonomyRate,
      direction: 'at_most',
    });
  }
  if (result.schemaRepairRate > gate.schemaRepairRate) {
    failures.push({
      metric: 'schemaRepairRate',
      observed: result.schemaRepairRate,
      required: gate.schemaRepairRate,
      direction: 'at_most',
    });
  }

  return failures;
}
